import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listAPBills, createAPBill, approveAPBill, recordAPBillPayment, batchPayAPBills } from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  PARTIALLY_PAID: 'bg-yellow-100 text-yellow-800',
  PAID: 'bg-green-100 text-green-700',
  OVERDUE: 'bg-red-100 text-red-700',
};

function APBillManagement({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentRef, setPaymentRef] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBills, setSelectedBills] = useState([]);
  const [formData, setFormData] = useState({
    supplier_name: '', bill_number: '', date: new Date().toISOString().slice(0, 10),
    due_date: '', description: '',
    items: [{ description: '', quantity: 1, unit_price: '', vat_amount: '' }],
  });

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchBills = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listAPBills(authAxios, statusFilter ? { status: statusFilter } : {});
      setBills(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load AP bills.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, statusFilter]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const addItem = () => setFormData(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, unit_price: '', vat_amount: '' }] }));
  const removeItem = (idx) => setFormData(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => setFormData(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [field]: value };
    return { ...f, items };
  });

  const formTotal = useMemo(() => formData.items.reduce((s, i) =>
    s + ((parseFloat(i.quantity) || 0) * (parseFloat(i.unit_price) || 0)) + (parseFloat(i.vat_amount) || 0), 0
  ), [formData.items]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createAPBill(authAxios, {
        ...formData,
        items: formData.items.filter(i => i.description && i.unit_price),
      });
      showFormMessage('success', 'AP bill created.');
      setShowForm(false);
      setFormData({ supplier_name: '', bill_number: '', date: new Date().toISOString().slice(0, 10), due_date: '', description: '', items: [{ description: '', quantity: 1, unit_price: '', vat_amount: '' }] });
      fetchBills();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create bill.');
    }
  };

  const handleApprove = async (id) => {
    const ok = await confirm({ title: 'Approve Bill', message: 'Approve this bill for payment?', confirmText: 'Approve' });
    if (!ok) return;
    try {
      await approveAPBill(authAxios, id);
      showFormMessage('success', 'Bill approved.');
      fetchBills();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Approval failed.'); }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      await recordAPBillPayment(authAxios, showPaymentModal, {
        amount: parseFloat(paymentAmount),
        payment_date: paymentDate,
        reference: paymentRef,
      });
      showFormMessage('success', 'Payment recorded.');
      setShowPaymentModal(null);
      setPaymentAmount('');
      setPaymentRef('');
      fetchBills();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Payment failed.'); }
  };

  const handleBatchPay = async () => {
    if (selectedBills.length === 0) { showFormMessage('error', 'Select bills to pay.'); return; }
    const ok = await confirm({ title: 'Batch Pay', message: `Pay ${selectedBills.length} selected bill(s) in full?`, confirmText: 'Pay All', variant: 'danger' });
    if (!ok) return;
    try {
      await batchPayAPBills(authAxios, { bill_ids: selectedBills });
      showFormMessage('success', `${selectedBills.length} bill(s) paid.`);
      setSelectedBills([]);
      fetchBills();
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Batch pay failed.'); }
  };

  const toggleSelect = (id) => setSelectedBills(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const filteredBills = useMemo(() => bills.filter(b => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (b.supplier_name || '').toLowerCase().includes(term) || (b.bill_number || '').toLowerCase().includes(term);
    }
    return true;
  }), [bills, searchTerm]);

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">AP Bills</h2>
          <p className="text-gray-500 text-sm mt-1">Manage accounts payable bills and payments</p>
        </div>
        <div className="flex gap-2">
          {canManage && selectedBills.length > 0 && (
            <button onClick={handleBatchPay} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium">
              Batch Pay ({selectedBills.length})
            </button>
          )}
          {canManage && (
            <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">
              + New Bill
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder="Search supplier or bill #..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Statuses</option>
          {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      {/* New Bill Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">New AP Bill</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <input type="text" required value={formData.supplier_name} onChange={e => setFormData({ ...formData, supplier_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Supplier name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Number</label>
                  <input type="text" value={formData.bill_number} onChange={e => setFormData({ ...formData, bill_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="INV-001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bill Date</label>
                  <input type="date" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input type="date" required value={formData.due_date} onChange={e => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Bill description" />
              </div>

              {/* Line items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Line Items</label>
                  <button type="button" onClick={addItem} className="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Add Item</button>
                </div>
                <div className="space-y-2">
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input type="text" placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                        className="col-span-4 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                      <input type="number" min="1" step="1" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <input type="number" step="0.01" min="0" placeholder="Unit Price" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <input type="number" step="0.01" min="0" placeholder="VAT" value={item.vat_amount} onChange={e => updateItem(idx, 'vat_amount', e.target.value)}
                        className="col-span-3 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-red-400 hover:text-red-600 text-sm">✕</button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-2 text-sm font-medium text-gray-700">
                  Total: {formatCurrency(formTotal)}
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">Create Bill</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                <input type="number" step="0.01" min="0.01" required value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                <input type="date" required value={paymentDate} onChange={e => setPaymentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input type="text" value={paymentRef} onChange={e => setPaymentRef(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Payment reference" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowPaymentModal(null)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium">Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bills list */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : filteredBills.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <p className="text-gray-500">No AP bills found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {canManage && <th className="px-4 py-3 w-10"><input type="checkbox" onChange={e => setSelectedBills(e.target.checked ? filteredBills.filter(b => b.status === 'APPROVED').map(b => b.id) : [])} className="rounded border-gray-300" /></th>}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredBills.map(b => (
                <tr key={b.id} className="hover:bg-gray-50">
                  {canManage && (
                    <td className="px-4 py-3">
                      {b.status === 'APPROVED' && (
                        <input type="checkbox" checked={selectedBills.includes(b.id)} onChange={() => toggleSelect(b.id)} className="rounded border-gray-300" />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 font-mono text-blue-600">{b.bill_number || `#${b.id}`}</td>
                  <td className="px-4 py-3 text-gray-800">{b.supplier_name}</td>
                  <td className="px-4 py-3 text-gray-600">{b.date}</td>
                  <td className="px-4 py-3 text-gray-600">{b.due_date}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(b.total_amount || b.amount || 0)}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(b.balance_due ?? b.total_amount ?? 0)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[b.status] || 'bg-gray-100 text-gray-600'}`}>
                      {(b.status || '').replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {canManage && b.status === 'DRAFT' && (
                      <button onClick={() => handleApprove(b.id)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Approve</button>
                    )}
                    {canManage && (b.status === 'APPROVED' || b.status === 'PARTIALLY_PAID') && (
                      <button onClick={() => { setShowPaymentModal(b.id); setPaymentAmount(String(b.balance_due ?? b.total_amount ?? '')); }}
                        className="text-green-600 hover:text-green-800 text-xs font-medium">Pay</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default APBillManagement;
